import React, { useCallback, useMemo } from 'react';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import PropTypes from 'prop-types';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { transformTableData } from 'utils';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'hooks';
import { useAsyncFn } from 'react-use';
import { deleteQueueJob, getQueueJobsList, restartQueueJob } from 'http/queueJobs';
import getTableConfig from './helpers/getTableConfig';

const JOB_STATUS_OPTIONS = ['pending', 'active', 'completed', 'failed'];
const JOB_TYPE_OPTIONS = [
  'moodle_course_copy',
  'moodle_course_results_fetch',
  'moodle_course_results_apply',
  'moodle_assessment_reconcile',
  'company_entity_upgrade_rebuild',
  'moodle_reconcile_run',
];
const POLL_INTERVAL_MS = 5000;

const Jobs = ({ tableData }) => {
  const { t } = useTranslation('common');
  const tableConfig = useMemo(() => getTableConfig(), [t]);
  const [columns, jobs] = transformTableData(tableConfig, tableData.data);
  const isLoading = tableData.isFetchingTableData;

  useInterval(() => {
    tableData.handleForceUpdate();
  }, POLL_INTERVAL_MS);

  const [, runRestart] = useAsyncFn(
    async id => {
      const result = await restartQueueJob(id);
      if (!result?.error) {
        tableData.handleForceUpdate();
      }
    },
    [tableData],
  );

  const [, runDelete] = useAsyncFn(
    async id => {
      const result = await deleteQueueJob(id);
      if (!result?.error) {
        tableData.handleForceUpdate();
      }
    },
    [tableData],
  );

  const handleRestart = useCallback(
    id => {
      runRestart(id);
    },
    [runRestart],
  );

  const handleDelete = useCallback(
    id => {
      runDelete(id);
    },
    [runDelete],
  );

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'replay',
        title: t('jobs.restart'),
        getDisabled: rowData => rowData.status !== 'failed',
        getOnClick: rowData => () => handleRestart(rowData.id),
      },
      {
        type: 'icon',
        icon: 'delete',
        title: t('jobs.delete'),
        getDisabled: rowData => !['failed', 'completed'].includes(rowData.status),
        getOnClick: rowData => () => handleDelete(rowData.id),
      },
    ],
    [t, handleRestart, handleDelete],
  );

  return (
    <PageLayout pageTitle={t('jobs.pageTitle')}>
      <TableFilterToolbar
        filters={[
          {
            type: 'singleSelect',
            name: 'status',
            label: t('tablesLabels.status'),
            options: JOB_STATUS_OPTIONS.map(value => ({
              value,
              label: t(`jobs.status.${value}`, { defaultValue: value }),
            })),
          },
          {
            type: 'singleSelect',
            name: 'jobType',
            label: t('tablesLabels.type'),
            options: JOB_TYPE_OPTIONS.map(value => ({
              value,
              label: t(`jobs.types.${value}`, { defaultValue: value }),
            })),
          },
        ]}
        filtering={tableData.filtering}
        onFilterChange={tableData.handleFiltering}
        onRemoveFilter={tableData.handleRemoveFilter}
        onClearFilters={tableData.handleClearFilters}
      />
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={jobs}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
          actions={tableActions}
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};

Jobs.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable(
  {
    defaultSorting: {
      field: 'createdAt',
      dir: 'desc',
    },
    sortingFieldsMapping: {
      status: 'statusSort',
    },
    allowedFilters: ['status', 'jobType'],
    fetchingData: 'items',
  },
  getQueueJobsList,
)(Jobs);
